import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Collapse,
  Divider,
  IconButton,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

import StepModify from './otherComponent/StepModify';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';
import AttachmentUploader from 'pages/announcements/components/AttachmentUploader';
import { AnnouncementAttachment } from 'types/auth';

type StatusItem = { _id: string; name: string; uid: string };
type NotificationPayload = { conditions: string[]; users: string[]; roles: string[]; channels: string[] };

type RedirectMode = 'Transaction Assignee' | 'qr' | 'manual' | 'home';

type RedirectFormProps = {
  selectedNodeData: any;
  selectedNode: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
};

// Columns the author can choose to show per candidate in `list` mode. These are a
// client-side display hint only — the backend returns the full assigned-transaction
// rows regardless.
const AVAILABLE_LIST_COLUMNS = ['key', 'displayName', 'displayStatus', 'dueAt', 'createdAt'];

// What a `home` step may carry: a document the user reads on the way out. Deliberately
// narrower than the uploader's default — a spreadsheet, a slide deck or a CSV is not
// something anyone reads on a phone mid-flow. Images and PDFs render inline in both
// clients; Word documents do not (see the note where the app renders this).
const HOME_DOC_TYPES: Record<string, string[]> = {
  'image/*': [],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
};
const DEFAULT_LIST_COLUMNS = ['key', 'displayName', 'displayStatus'];

/**
 * Redirect component config.
 *
 * A Redirect step lets the user jump to ANOTHER transaction they are assigned to,
 * chosen at runtime via a single picker (list OR QR OR manual). Nothing here
 * configures the destination — only WHICH picker is shown and how the list looks.
 *
 * The `home` mode is the exception: it resolves no destination at all. It completes
 * the step and sends the user back to the dashboard, optionally showing a document
 * (PDF or image) on the way out.
 *
 * Everything lands in `component.settings.redirect`. The component carries exactly
 * one auto-provisioned field flagged `isRedirectTarget` — the slot the runtime
 * selection lands in; the backend reads it to resolve and record the redirect.
 * The home-mode document lands in `component.settings.attachments` — a SIBLING of
 * `redirect`, not nested inside it, because that is the key the backend's generic
 * `FilesService.signAttachments()` signs on every step read.
 */
const RedirectForm = memo(({ selectedNodeData, onSubmit, onClose, selectedNode }: RedirectFormProps) => {
  const [initialValues, setInitialValues] = useState<any | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: [],
  });
  const [escalationPayload, setEscalationPayload] = useState<any>({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] },
  });

  // ── Redirect config, held outside Formik ──
  const [mode, setMode] = useState<RedirectMode>('Transaction Assignee');
  const [prompt, setPrompt] = useState<string>('');
  const [listColumns, setListColumns] = useState<string[]>(DEFAULT_LIST_COLUMNS);
  // Held outside Formik and merged in on submit — same approach Instructions.tsx and
  // the announcement form use with AttachmentUploader.
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);

  const handleStatusesLoaded = useCallback((list: StatusItem[]) => setStatuses(list), []);
  const handleNotificationChange = useCallback((payload: NotificationPayload) => setNotificationPayload(payload), []);
  const handleEscalationChange = useCallback((payload: any) => setEscalationPayload(payload), []);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    const type = newType.toUpperCase();
    setFieldValue('stepType', type);
    setFieldValue('componentType', type);
    setFieldValue('data.title', type);
    setTimeout(() => submitForm());
  }, []);

  useEffect(() => {
    if (!selectedNodeData) return;

    const {
      name = selectedNodeData.component?.name || '',
      title,
      description,
      settings,
      component = {},
      componentType,
      data,
      onComplete,
    } = selectedNodeData;

    const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];
    const redirect = settings?.redirect || {};
    // Back-compat: an older step may carry a `modes` array — take its first entry.
    // The fallback is the list mode's current literal: a step saved before the rename
    // stores 'list', which matches no radio, so it would open with nothing selected.
    const savedMode: RedirectMode =
      redirect.mode || (Array.isArray(redirect.modes) ? redirect.modes[0] : undefined) || 'Transaction Assignee';

    setMode(savedMode === ('list' as RedirectMode) ? 'Transaction Assignee' : savedMode);
    setPrompt(redirect.prompt ?? '');
    setListColumns(
      Array.isArray(redirect.listColumns) && redirect.listColumns.length > 0
        ? redirect.listColumns
        : DEFAULT_LIST_COLUMNS,
    );
    setAttachments(Array.isArray(settings?.attachments) ? (settings.attachments as AnnouncementAttachment[]) : []);

    setInitialValues({
      name: name || 'Redirect',
      title: title || 'Redirect',
      description: description || '',
      component,
      componentType: componentType || 'REDIRECT',
      data: {
        title: selectedNode?.data?.label || data?.title || 'Redirect',
        step: { ...(data?.step || {}), actor, componentType: componentType || 'REDIRECT' },
      },
      actor,
      stepType: selectedNode?.data?.step?.stepType || 'REDIRECT',
      onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete: onComplete || {},
    });

    const backendNotification = selectedNode?.data?.step?.notification || {};
    setNotificationPayload({
      conditions: backendNotification.conditions || [],
      users: backendNotification.users || [],
      roles: backendNotification.roles || [],
      channels: backendNotification.channels || [],
    });

    // Pass the stored object straight through. This used to be rebuilt by hand
    // from a fixed list of keys, which silently dropped any field it did not
    // know about — including `inherit`, so opening and saving any step would
    // reset it to the process default.
    setEscalationPayload(selectedNode?.data?.step?.escalation || {});
  }, [
    selectedNodeData?.componentType,
    selectedNodeData?.name,
    selectedNodeData?.description,
    selectedNodeData?.settings,
    selectedNode?.data?.label,
    selectedNode?.data?.step?.actor,
    selectedNode?.data?.step?.notification,
    selectedNode?.data?.step?.escalation,
    selectedNode?.data?.step?.onComplete?.status?.uid,
  ]);

  const notificationInitialValues = useMemo(() => {
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels || [],
    };
  }, [selectedNode?.data?.step?.notification]);

  const MODE_OPTIONS: { value: RedirectMode; label: string; description: string }[] = [
    { value: 'Transaction Assignee', label: 'Assigned Transactions', description: "Show the user's assigned transactions to pick from." },
    { value: 'qr', label: 'QR code', description: "Scan a transaction's QR code to redirect to it." },
    { value: 'manual', label: 'Manual entry', description: 'Type a transaction code to redirect to it.' },
    { value: 'home', label: 'Home', description: 'Finish the step and return the user to the dashboard, optionally showing a document first.' },
  ];

  if (!initialValues) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography>Loading Redirect…</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);

        // Preserve the existing target field (keeps its uid stable across edits);
        // otherwise auto-provision the single isRedirectTarget field the runtime
        // selection lands in.
        const existingFields: any[] = Array.isArray(values.component?.fields) ? values.component.fields : [];
        let targetField = existingFields.find((f) => f?.settings?.isRedirectTarget);
        if (!targetField) {
          targetField = {
            question: 'Destination transaction',
            description: null,
            inputComponent: 'TEXT',
            options: [],
            settings: { isRedirectTarget: true },
            resourceType: null,
            resourceList: [],
            children: [],
            uid: uuidv4(),
          };
        }

        const payload = {
          ...values,
          settings: {
            redirect: {
              mode,
              prompt: prompt.trim(),
              listColumns: mode === 'Transaction Assignee' ? listColumns : [],
            },
            // `url` is a short-lived preview URL the API rejects, and `media_type` is
            // derived server-side from the mime type — except on a pasted link, which
            // has no mime type and states its own kind. Same strip Instructions.tsx uses.
            attachments:
              mode === 'home'
                ? attachments.map(({ url, media_type, ...rest }: any) => (rest.is_link ? { ...rest, media_type } : rest))
                : [],
          },
          component: {
            ...values.component,
            name: values.name,
            componentType: 'REDIRECT',
            fields: [targetField],
          },
          onComplete: selectedStatus ? { status: selectedStatus } : {},
        };

        onSubmit({ ...payload, notification: notificationPayload, escalation: escalationPayload });
        setSubmitting(false);
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, submitForm }) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            height: '100%',
            overflowY: 'auto',
            // Keep the scrollbar visible (it otherwise auto-hides on macOS).
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.04)' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.28)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.42)' },
          }}
        >
          <Paper elevation={0} sx={{ p: 2, width: '100%' }}>
            <Box display="flex" justifyContent="center" alignItems="center" mb={3} borderBottom={1} borderColor="grey.400" pb={2}>
              {isEditingTitle ? (
                <Box display="flex" alignItems="center" sx={{ width: '100%', maxWidth: '600px' }}>
                  <TextField
                    name="data.title"
                    value={values.data?.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    fullWidth
                    sx={{ mr: 2, textTransform: 'uppercase' }}
                  />
                  <Button variant="contained" size="small" onClick={() => setIsEditingTitle(false)}>
                    save
                  </Button>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}
                      title={values.data?.title || values.stepType}
                    >
                      {values.data?.title || 'Redirect'}
                    </Typography>
                    <IconButton size="small" onClick={() => setIsEditingTitle(true)} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {selectedNode && (
                    <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                      <StepModify
                        selectedNode={selectedNode}
                        onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    At this step the user picks another transaction assigned to them and is redirected to it. Choose
                    the one selection method to offer below. The destination is always chosen at runtime — never
                    configured here. Pick <b>Home</b> instead to end the step and send the user back to the
                    dashboard rather than on to another transaction.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Prompt (optional)"
                    placeholder="e.g. Select the transaction to transfer to"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selection method
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as RedirectMode)}>
                      {MODE_OPTIONS.map((opt) => (
                        <Box
                          key={opt.value}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: mode === opt.value ? 'rgba(30,142,78,0.25)' : 'divider',
                            bgcolor: mode === opt.value ? 'rgba(30,142,78,0.06)' : 'grey.50',
                            px: 2,
                            py: 1,
                            mb: 1,
                          }}
                        >
                          <FormControlLabel
                            value={opt.value}
                            control={<Radio color="success" />}
                            label={
                              <Box>
                                <Typography fontWeight={600}>{opt.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {opt.description}
                                </Typography>
                              </Box>
                            }
                          />
                          {opt.value === 'home' && (
                            <Collapse in={mode === 'home'} unmountOnExit>
                              <Box sx={{ pl: 4, pb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Document (optional) — shown to the user before they return to the dashboard
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <AttachmentUploader
                                    value={attachments}
                                    onChange={setAttachments}
                                    accept={HOME_DOC_TYPES}
                                    hint="or click to browse — images, PDF, Word"
                                  />
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                          {opt.value === 'Transaction Assignee' && (
                            <Collapse in={mode === 'Transaction Assignee'} unmountOnExit>
                              <Box sx={{ pl: 4, pb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Columns shown per transaction
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                  {AVAILABLE_LIST_COLUMNS.map((col) => (
                                    <FormControlLabel
                                      key={col}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={listColumns.includes(col)}
                                          onChange={(e) =>
                                            setListColumns(
                                              e.target.checked
                                                ? [...listColumns, col]
                                                : listColumns.filter((c) => c !== col),
                                            )
                                          }
                                        />
                                      }
                                      label={col}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <CommonNodeSetting
                    selectedNode={selectedNode}
                    onStatusesLoaded={handleStatusesLoaded}
                    onNotificationChange={handleNotificationChange}
                    notificationInitialValues={notificationInitialValues}
                    escalationPayload={escalationPayload}
                    onEscalationChange={handleEscalationChange}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button type="button" color="error" sx={{ py: 1, fontSize: '1rem' }} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ py: 1, fontSize: '1rem' }}
                >
                  Save &amp; Close
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Formik>
  );
});

export default RedirectForm;
